// FILE PATH: eRFQFrontend/src/component/Logistic/ViewLogisticQuotesExcelExport.jsx

import React from "react";
import {
  buildExchangeRateMap,
  buildVendorSummaryGroups,
  formatAmount,
  getQuoteTotalsByCurrency,
  getQuoteTotalInInr,
  getSummaryCurrencies,
  shortCurrency,
} from "./logisticSummaryUtils";
import { Box, Typography } from "@mui/material";

const ViewLogisticQuotesExcelExport = ({ quotes = [], logisticDraft, currencyArray = []}) => {
  if (!quotes.length) return null;

  const getItemKey = (item) => [item?.particulars ?? ""].join("__");

  const parseBuyerWorkItems = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const buyerWorkItems = parseBuyerWorkItems(logisticDraft?.workItems);

  const findBuyerItem = (refItem) =>
    buyerWorkItems.find((item, index) => {
      const buyerSrNo = item?.srNo ?? item?.itemOrder ?? index + 1;
      const refSrNo = refItem?.srNo ?? refItem?.itemOrder ?? null;

      if (refSrNo !== null && buyerSrNo === refSrNo) return true;

      return (
        String(item?.particulars || "").trim() ===
        String(refItem?.particulars || "").trim()
      );
    });

  const refItems = Array.from(
    new Map(
      quotes
        .flatMap((q) => q.quoteItems || [])
        .sort((a, b) => {
          const srNoDiff = (a.srNo || 0) - (b.srNo || 0);
          if (srNoDiff !== 0) return srNoDiff;

          return getItemKey(a).localeCompare(getItemKey(b));
        })
        .map((item) => [getItemKey(item), item]),
    ).values(),
  );

  const detailValue = (q, key) => {
    const value = q?.[key];
    if (value !== null && value !== undefined && value !== "") return value;
    const draftVal = logisticDraft?.[key];
    if (draftVal !== null && draftVal !== undefined && draftVal !== "")
      return draftVal;
    return "-";
  };

  const showRemarkByQuoteId = Object.fromEntries(
    quotes.map((quote) => [
      quote.logisticQuoteId,
      (quote?.quoteItems || []).some(
        (item) => String(item?.remark || "").trim() !== "",
      ),
    ]),
  );

  const detailExchangeRateMap = Object.fromEntries(
    (Array.isArray(logisticDraft?.currencies) ? logisticDraft.currencies : [])
      .filter((currency) => currency?.name)
      .flatMap((currency) => {
        const name = String(currency.name).trim();
        return [
          [name, Number(currency.rate || 0)],
          [shortCurrency(name), Number(currency.rate || 0)],
        ];
      }),
  );

  const getCurrencyAmount = (item) =>
    Number(item?.quantity || 0) * Number(item?.rate || 0);

  const getQuoteCurrencyTotal = (quote, currency) =>
    (quote?.quoteItems || [])
      .filter((item) => item?.currency === currency)
      .reduce((sum, item) => sum + getCurrencyAmount(item), 0);

  const getQuoteInrTotal = (quote) =>
    (quote?.quoteItems || []).reduce((sum, item) => {
      const itemTotal = Number(item?.total || 0);
      if (itemTotal > 0) return sum + itemTotal;

      const currency = item?.currency || "";
      const exchangeRate = Number(
        item?.exchangeRate ??
          detailExchangeRateMap?.[currency] ??
          detailExchangeRateMap?.[shortCurrency(currency)] ??
          0,
      );

      return sum + getCurrencyAmount(item) * exchangeRate;
    }, 0);

  const detailGrossWeight = Number(logisticDraft?.grossWeight || 0);
  const latestQuoteTotals = quotes
    .filter((quote) => quote?.isCurrentVersion !== false)
    .map((quote) => ({
      logisticQuoteId: quote.logisticQuoteId,
      totalInInr: getQuoteInrTotal(quote),
    }))
    .filter((quote) => quote.totalInInr > 0)
    .sort((a, b) => a.totalInInr - b.totalInInr);

  const lowestQuote = latestQuoteTotals[0] || null;
  const secondLowestQuote =
    latestQuoteTotals.find(
      (quote) => quote.logisticQuoteId !== lowestQuote?.logisticQuoteId,
    ) || null;

  const vendorGroups = buildVendorSummaryGroups(quotes);
  const allCurrencies = getSummaryCurrencies(quotes);
  const exchangeRateMap = buildExchangeRateMap(logisticDraft);
  const grossWeight = Number(logisticDraft?.grossWeight || 0);

  const renderCurrencyValue = (quote, currency) => {
    if (!quote) return "-";
    const totals = getQuoteTotalsByCurrency(quote);
    const amount = Number(totals[currency] || 0);
    return amount > 0
      ? `${shortCurrency(currency)} ${formatAmount(amount)}`
      : "-";
  };

  const renderInrValue = (quote) => {
    if (!quote) return "-";
    const totalInInr = getQuoteTotalInInr(quote, exchangeRateMap);
    return totalInInr > 0 ? `INR ${formatAmount(totalInInr)}` : "-";
  };

  const renderCostPerKgValue = (quote) => {
    if (!quote || grossWeight <= 0) return "-";
    const totalInInr = getQuoteTotalInInr(quote, exchangeRateMap);
    return totalInInr > 0
      ? `INR ${formatAmount(totalInInr / grossWeight)}`
      : "-";
  };

  return (
    <div>
      {currencyArray.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 3 }}>
            Currency Exchange Rates
          </Typography>
          <Box className="dashboard-container" px={10}>
            <table
              border={1}
              style={{
                borderCollapse: "collapse",
                width: "100%",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "20px" }}>Currency</th>
                  <th>Exchange Rate</th>
                </tr>
              </thead>
              <tbody>
                {currencyArray.map((currency, index) => (
                  <tr key={index} style={{ textAlign: "center" }}>
                    <td style={{ padding: "20px" }}>{currency.name || "-"}</td>
                    <td>{currency.rate ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </>
      )}
      {logisticDraft && (
        <>
          <Typography variant="h6" sx={{ mt: 3 }}>
            Common Logistics Details
          </Typography>
          <Box className="dashboard-container" px={10}>
            <table
              border={1}
              style={{
                borderCollapse: "collapse",
                width: "100%",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "20px", width: "40%" }}>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Mode of Consignment", logisticDraft.modeOfConsignment],
                  ["Invoice No", logisticDraft.invoiceNo],
                  ["Origin", logisticDraft.origin],
                  ["Destination", logisticDraft.destination],
                  ["Consignee", logisticDraft.consignee],
                  ["Transit Time", withUnit(logisticDraft.transitTime, "Days")],
                  [
                    "Gross Weight (kg)",
                    withUnit(logisticDraft.grossWeight, "Kg"),
                  ],
                  ["Length", withUnit(logisticDraft.length, "inches")],
                  ["Breadth", withUnit(logisticDraft.breadth, "inches")],
                  ["Height", withUnit(logisticDraft.height, "inches")],
                  ["Volume (CBM)", withUnit(logisticDraft.volumeCBM, "CBM")],
                  ["No. of Pallets", logisticDraft.noOfPallets],
                  ["Address", logisticDraft.address],
                ].map(([label, value]) => (
                  <tr key={label} style={{ textAlign: "center" }}>
                    <td
                      style={{
                        padding: "20px",
                        fontWeight: "bold",
                        textAlign: "left",
                      }}
                    >
                      {label}
                    </td>
                    <td>{value ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </>
      )}
      <table border="1">
        <thead>
          <tr>
            <th>Vendor</th>
            {vendorGroups.map((group) => (
              <th key={group.vendorKey} colSpan={group.finalQuote ? 2 : 1}>
                {group.vendorLabel}
              </th>
            ))}
          </tr>
          <tr>
            <th></th>
            {vendorGroups.map((group) => (
              <React.Fragment key={`${group.vendorKey}-summary-columns`}>
                <th>Initial Quote</th>
                {group.finalQuote && <th>Final Quote</th>}
              </React.Fragment>
            ))}
          </tr>
        </thead>

        <tbody>
          {allCurrencies.map((currency) => (
            <tr key={`summary-${currency}`}>
              <td>{`Grand Total (${currency})`}</td>
              {vendorGroups.map((group) => (
                <React.Fragment key={`${group.vendorKey}-${currency}`}>
                  <td>{renderCurrencyValue(group.initialQuote, currency)}</td>
                  {group.finalQuote && (
                    <td>{renderCurrencyValue(group.finalQuote, currency)}</td>
                  )}
                </React.Fragment>
              ))}
            </tr>
          ))}

          <tr>
            <td>Total (INR - Indian Rupee)</td>
            {vendorGroups.map((group) => (
              <React.Fragment key={`${group.vendorKey}-summary-inr`}>
                <td>{renderInrValue(group.initialQuote)}</td>
                {group.finalQuote && (
                  <td>{renderInrValue(group.finalQuote)}</td>
                )}
              </React.Fragment>
            ))}
          </tr>

          <tr>
            <td>Cost per Kg</td>
            {vendorGroups.map((group) => (
              <React.Fragment key={`${group.vendorKey}-summary-kg`}>
                <td>{renderCostPerKgValue(group.initialQuote)}</td>
                {group.finalQuote && (
                  <td>{renderCostPerKgValue(group.finalQuote)}</td>
                )}
              </React.Fragment>
            ))}
          </tr>

          <tr></tr>

          <tr>
            <th rowSpan={2}>Sr No</th>
            <th rowSpan={2}>Particulars</th>
            <th rowSpan={2}>UOM</th>
            <th rowSpan={2}>Quantity</th>
            {quotes.map((q) => (
              <th
                key={q.logisticQuoteId}
                colSpan={showRemarkByQuoteId[q.logisticQuoteId] ? 6 : 5}
              >
                {q.comparisonLabel || q.vendorFirmName || q.vendorName}
              </th>
            ))}
          </tr>

          <tr>
            {quotes.map((q) => (
              <React.Fragment key={q.logisticQuoteId}>
                <th>UOM</th>
                <th>Quantity</th>
                <th>Currency</th>
                <th>Rate</th>
                <th>Total</th>
                {showRemarkByQuoteId[q.logisticQuoteId] && <th>Remark</th>}
              </React.Fragment>
            ))}
          </tr>

          {refItems
            .sort((a, b) => a.srNo - b.srNo)
            .map((refItem, idx) => (
              <tr key={idx}>
                {(() => {
                  const buyerItem = findBuyerItem(refItem);
                  return (
                    <>
                      <td>{idx + 1}</td>
                      <td>{refItem.particulars || "-"}</td>
                      <td>{buyerItem?.unit || "-"}</td>
                      <td>{buyerItem?.quantity ?? "-"}</td>

                      {quotes.map((q) => {
                        const item = q.quoteItems?.find(
                          (it) => getItemKey(it) === getItemKey(refItem),
                        );

                        return (
                          <React.Fragment key={`${q.logisticQuoteId}-${idx}`}>
                            <td>{item?.unit || "-"}</td>
                            <td>{item?.quantity ?? "-"}</td>
                            <td>{item?.currency || "-"}</td>
                            <td>
                              {item?.rate != null
                                ? formatAmount(item.rate)
                                : "-"}
                            </td>
                            <td>
                              {item?.total != null
                                ? formatAmount(item.total)
                                : "-"}
                            </td>
                            {showRemarkByQuoteId[q.logisticQuoteId] && (
                              <td
                                style={{
                                  maxWidth: "100px",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {item?.remark?.trim() || "-"}
                              </td>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </>
                  );
                })()}
              </tr>
            ))}

          {allCurrencies.map((cur) => (
            <tr key={`details-${cur}`}>
              <td colSpan={4}>
                <b>{`Grand Total (${cur})`}</b>
              </td>
              {quotes.map((q) => (
                <td
                  key={q.logisticQuoteId + cur}
                  colSpan={showRemarkByQuoteId[q.logisticQuoteId] ? 6 : 5}
                >
                  <b>{`${shortCurrency(cur)} ${formatAmount(getQuoteCurrencyTotal(q, cur))}`}</b>
                </td>
              ))}
            </tr>
          ))}

          <tr>
            <td colSpan={4}>
              <b>Total (INR - Indian Rupee)</b>
            </td>
            {quotes.map((q) => (
              <td
                key={`${q.logisticQuoteId}-details-inr`}
                colSpan={showRemarkByQuoteId[q.logisticQuoteId] ? 6 : 5}
              >
                <b>{`INR ${formatAmount(getQuoteInrTotal(q))}`}</b>
              </td>
            ))}
          </tr>

          <tr>
            <td colSpan={4}>
              <b>Cost per Kg</b>
            </td>
            {quotes.map((q) => (
              <td
                key={`${q.logisticQuoteId}-details-cost`}
                colSpan={showRemarkByQuoteId[q.logisticQuoteId] ? 6 : 5}
              >
                <b>
                  {detailGrossWeight > 0
                    ? `INR ${formatAmount(getQuoteInrTotal(q) / detailGrossWeight)}`
                    : "-"}
                </b>
              </td>
            ))}
          </tr>

          <tr>
            <td colSpan={4}>
              <b>Commercial Rating</b>
            </td>
            {quotes.map((q) => {
              const isLatest = q?.isCurrentVersion !== false;
              return (
                <td
                  key={`${q.logisticQuoteId}-details-rank`}
                  colSpan={showRemarkByQuoteId[q.logisticQuoteId] ? 6 : 5}
                >
                  <b>
                    {isLatest && getQuoteInrTotal(q) === lowestQuote?.totalInInr
                      ? "L1"
                      : isLatest &&
                          getQuoteInrTotal(q) === secondLowestQuote?.totalInInr
                        ? "L2"
                        : ""}
                  </b>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default ViewLogisticQuotesExcelExport;

const withUnit = (value, unit) => {
  if (value === null || value === undefined || value === "") return "";
  return `${value} ${unit}`;
};